import React, { useState, useId } from 'react';
import { AccountForm } from './AccountForm';
import { useDialog } from '../hooks/useDialog';
import { Icon } from './Icon';

export const AccountSelector = ({
  accounts,
  currentIndex,
  onSwitchAccount,
  onRemoveAccount,
  onAddAccount
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const titleId = useId();

  const closeMenu = () => {
    setMenuOpen(false);
    setShowAddForm(false);
  };

  const panelRef = useDialog(menuOpen, closeMenu);

  const handleAdd = (name, token) => {
    onAddAccount(name, token);
    closeMenu();
  };

  const handleSwitch = (index) => {
    onSwitchAccount(index);
    closeMenu();
  };

  // The panel closes first so the confirmation dialog is the only thing open.
  const handleRemove = (index) => {
    closeMenu();
    onRemoveAccount(index);
  };

  const currentName = accounts.length > 0 ? accounts[currentIndex]?.name : 'Accounts';

  return (
    <div className="account-selector-container">
      <button
        type="button"
        className="account-menu-toggle"
        onClick={() => setMenuOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        aria-label={`Manage accounts. Current account: ${currentName}`}
      >
        <Icon name="accounts" />
        <span className="current-account-name">{currentName}</span>
        <span className="menu-icon"><Icon name="chevronDown" size={14} /></span>
      </button>

      {/* Slide-out Account Panel */}
      <div
        ref={panelRef}
        className={`account-panel ${menuOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-hidden={!menuOpen}
        tabIndex={-1}
      >
        <div className="panel-header">
          <h2 id={titleId}>Account Manager</h2>
          <button type="button" className="close-panel" onClick={closeMenu} aria-label="Close account manager">×</button>
        </div>

        {/* Current Account Info */}
        {accounts.length > 0 && (
          <div className="current-account-info">
            <h3>Current Account</h3>
            <div className="account-badge">
              {accounts[currentIndex]?.name || 'Unknown'}
            </div>
          </div>
        )}

        {/* Account Actions */}
        <div className="account-actions">
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className={showAddForm ? 'btn-secondary add-account-toggle' : 'add-account-btn'}
            aria-expanded={showAddForm}
          >
            {showAddForm ? 'Cancel Adding' : 'Add Account'}
          </button>

          {accounts.length > 1 && (
            <div className="account-switcher">
              <label htmlFor="accountDropdown">Switch to:</label>
              <select
                id="accountDropdown"
                value={currentIndex}
                onChange={(e) => handleSwitch(Number.parseInt(e.target.value, 10))}
                className="account-dropdown"
              >
                {accounts.map((account, index) => (
                  <option key={account.name} value={index}>
                    {account.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Add Account Form */}
        {showAddForm && (
          <div className="add-account-form">
            <h3>Add New Account</h3>
            <AccountForm
              onSubmit={handleAdd}
              existingNames={accounts.map((account) => account.name)}
            />
          </div>
        )}

        {/* Accounts List */}
        {accounts.length > 0 && (
          <div className="accounts-list">
            <h3>Manage Accounts</h3>
            <ul>
              {accounts.map((account, index) => (
                <li key={account.name} className={index === currentIndex ? 'active' : ''}>
                  <span className="account-name">
                    {account.name}
                    {account.managed && (
                      <span className="account-tag" title="Configured on the server">Server</span>
                    )}
                  </span>
                  <div className="account-controls">
                    <button
                      type="button"
                      onClick={() => handleSwitch(index)}
                      disabled={index === currentIndex}
                      className="switch-btn"
                      aria-label={index === currentIndex ? `${account.name} is the current account` : `Switch to ${account.name}`}
                    >
                      {index === currentIndex ? 'Current' : 'Switch'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="remove-btn"
                      disabled={account.managed}
                      title={account.managed ? 'This account is configured on the server and cannot be removed here' : undefined}
                      aria-label={`Remove ${account.name}`}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {menuOpen && (
        <div
          className="account-panel-overlay"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
